<template>
  <Dialog
    v-model:visible="visible"
    :header="activeLeaveBlock?.title ?? 'Unsaved Changes'"
    class="max-w-sm"
    :draggable="false"
    modal
    @hide="onHide"
  >
    <p v-if="activeLeaveBlock" id="leave-block-message">{{ activeLeaveBlock.message }}</p>
    <slot v-else name="description">
      There are unsaved changes. Do you want to save changes or discard before leaving?
    </slot>
    <ErrorDisplay v-if="saveFailure.message" v-bind="saveFailure" />
    <template #footer>
      <Button
        v-if="activeLeaveBlock"
        id="keep-editing-button"
        @click="closePrompt"
        severity="secondary"
      >
        Keep Editing
      </Button>
      <Button v-else id="discard-button" @click="discardChanges" severity="secondary">
        Discard
      </Button>
      <!-- Blocked mode leaves Save as the only action, so hide it when there is nothing to write. -->
      <Button
        v-if="!activeLeaveBlock || hasUpdates"
        id="save-button"
        @click="saveChanges"
        :disabled="!validToSubmit || isSaving"
      >
        {{ isSaving ? 'Saving...' : saveButtonLabel }}
      </Button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { ref, toRefs, watch } from 'vue'
import { RouteLocationNormalized } from 'vue-router'

import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import { useUnsavedChangesGuard } from '@/common/composables/useUnsavedChangesGuard'
import type { GetLeaveBlock } from '@/common/interfaces/UnsavedChangesInterfaces'
import { type ErrorData, blankError, clearError, displayHandled } from '@/utils/errors'

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (e: 'discardUpdates'): void
}>()
const props = withDefaults(
  defineProps<{
    hasUpdates: boolean
    // eslint-disable-next-line no-unused-vars
    withinValidRoutes: (to: RouteLocationNormalized, from: RouteLocationNormalized) => boolean
    saveUpdates?: () => Promise<void>
    validToSubmit?: boolean
    preventNavigationOnSave?: boolean
    saveButtonLabel?: string
    getLeaveBlock?: GetLeaveBlock
  }>(),
  {
    validToSubmit: true,
    saveButtonLabel: 'Save',
    saveUpdates: undefined,
    preventNavigationOnSave: false,
    getLeaveBlock: undefined,
  },
)
const { hasUpdates, validToSubmit, preventNavigationOnSave } = toRefs(props)

const {
  visible,
  resolve,
  activeLeaveBlock,
  isNavigating,
  openModal,
  settleNavigation,
  closePrompt,
  preventNavigationWhenUnsavedChanges,
} = useUnsavedChangesGuard({
  hasUpdates: () => hasUpdates.value,
  withinValidRoutes: (to, from) => props.withinValidRoutes(to, from),
  getLeaveBlock: () => props.getLeaveBlock?.() ?? null,
})

const isSaving = ref<boolean>(false)
const saveFailure = ref<ErrorData>(blankError())

// A fresh prompt starts clean: a failure from the last one describes an attempt this venue has since
// walked away from. Watched rather than cleared on close, so it holds however the prompt was opened.
watch(visible, function clearStaleFailure(open: boolean): void {
  if (open) clearError(saveFailure.value)
})

function discardChanges(): void {
  emit('discardUpdates')
  // Answered before the prompt closes: closing emits `hide`, which answers `false` for a dismissal, and
  // only the order keeps that from cancelling the leave the venue just asked for.
  settleNavigation(true)
  closePrompt()
}

async function saveChanges(): Promise<void> {
  if (!validToSubmit.value || isSaving.value) return
  isSaving.value = true
  clearError(saveFailure.value)
  try {
    await props.saveUpdates?.()
  } catch (error) {
    // Otherwise the venue sits on "Saving..." with a navigation nobody will ever answer.
    displayHandled(error, saveFailure.value)
    return
  } finally {
    isSaving.value = false
  }
  settleNavigation(!preventNavigationOnSave.value)
  closePrompt()
}

// A dismissed prompt still owes the router an answer: without one the navigation hangs and the address
// bar keeps a leave that never happened.
function onHide(): void {
  activeLeaveBlock.value = null
  settleNavigation(false)
}

// Lets specs drive the guard directly instead of staging a real navigation for every case.
defineExpose({
  visible,
  resolve,
  isNavigating,
  activeLeaveBlock,
  openModal,
  settleNavigation,
  closePrompt,
  preventNavigationWhenUnsavedChanges,
})
</script>
